/** Shared SSRF IP policy — used for literal hostname checks and post-DNS resolution guards. */
const BLOCKED_HOSTNAMES = new Set([
    "localhost",
    "localhost.localdomain",
    "metadata.google.internal",
    "metadata",
]);
const PRIVATE_IPV4_RANGES = [
    { start: ipToInt("10.0.0.0"), end: ipToInt("10.255.255.255") },
    { start: ipToInt("127.0.0.0"), end: ipToInt("127.255.255.255") },
    { start: ipToInt("169.254.0.0"), end: ipToInt("169.254.255.255") },
    { start: ipToInt("172.16.0.0"), end: ipToInt("172.31.255.255") },
    { start: ipToInt("192.168.0.0"), end: ipToInt("192.168.255.255") },
    { start: ipToInt("0.0.0.0"), end: ipToInt("0.255.255.255") },
];
export function ipToInt(ip) {
    const parts = ip.split(".").map((p) => Number(p));
    if (parts.length !== 4 || parts.some((p) => !Number.isInteger(p) || p < 0 || p > 255)) {
        return -1;
    }
    return ((parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3]) >>> 0;
}
function parseIpv4MappedTail(tail) {
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(tail)) {
        return tail;
    }
    const hexMatch = /^([0-9a-f]{1,4}):([0-9a-f]{1,4})$/i.exec(tail);
    if (!hexMatch) {
        return null;
    }
    const high = Number.parseInt(hexMatch[1], 16);
    const low = Number.parseInt(hexMatch[2], 16);
    if (!Number.isFinite(high) || !Number.isFinite(low)) {
        return null;
    }
    return `${(high >> 8) & 0xff}.${high & 0xff}.${(low >> 8) & 0xff}.${low & 0xff}`;
}
/** Normalize IPv4-mapped IPv6 forms to IPv4 when applicable. */
export function normalizeIpAddress(address) {
    const trimmed = address.trim().toLowerCase();
    if (trimmed.startsWith("::ffff:")) {
        const mapped = parseIpv4MappedTail(trimmed.slice("::ffff:".length));
        if (mapped) {
            return mapped;
        }
    }
    return trimmed;
}
export function isPrivateOrReservedIpv4(host) {
    if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
        return false;
    }
    const value = ipToInt(host);
    if (value < 0)
        return true;
    return PRIVATE_IPV4_RANGES.some((range) => value >= range.start && value <= range.end);
}
export function isBlockedIpv6(host) {
    const normalized = host.toLowerCase();
    if (normalized === "::1")
        return true;
    if (normalized.startsWith("fc") || normalized.startsWith("fd"))
        return true;
    if (normalized.startsWith("fe80:"))
        return true;
    if (normalized.startsWith("::ffff:")) {
        const mapped = parseIpv4MappedTail(normalized.slice("::ffff:".length));
        if (mapped && isPrivateOrReservedIpv4(mapped)) {
            return true;
        }
    }
    return false;
}
export function isBlockedHostname(hostname) {
    const lower = hostname.toLowerCase();
    if (BLOCKED_HOSTNAMES.has(lower))
        return true;
    if (lower.endsWith(".localhost") || lower.endsWith(".local"))
        return true;
    return false;
}
/** Returns unsafe reason when address must not be connected to; null when safe. */
export function classifyResolvedIpAddress(address) {
    const normalized = normalizeIpAddress(address);
    if (isPrivateOrReservedIpv4(normalized)) {
        return "private_host";
    }
    if (normalized.includes(":") && isBlockedIpv6(normalized)) {
        return "private_host";
    }
    if (!normalized.includes(":") && !/^\d{1,3}(\.\d{1,3}){3}$/.test(normalized)) {
        return "invalid_ip";
    }
    return null;
}
export function assertResolvedIpAddressesSafe(addresses) {
    for (const address of addresses) {
        const reason = classifyResolvedIpAddress(address);
        if (reason) {
            throw new Error(`ssrf_blocked:${reason}`);
        }
    }
}
//# sourceMappingURL=ip-address-policy.js.map